import { useState } from 'react'
import { Info, Plane, Plus } from 'lucide-react'
import {
  CurrencyValue, EmptyState, ErrorState, FormDialog, PageHeader, StatusBadge, EmployeePicker } from '@/components/common'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { useBreadcrumb } from '@/hooks/useBreadcrumb'
import { toApiProblem } from '@/lib/api/client'
import { usePerDiemRates, useTravelMutations, useTravelRequests } from '../api'
import { TRAVEL_STATUS_LABELS } from '../types'
import type { TravelStatus } from '../types'
import { DatePicker } from '@/components/ui/date-picker'

const STATUS_TONE: Record<TravelStatus, 'neutral' | 'info' | 'success' | 'warning' | 'danger'> = {
  DRAFT: 'neutral',
  SUBMITTED: 'warning',
  APPROVED: 'info',
  REJECTED: 'danger',
  COMPLETED: 'success',
  CANCELLED: 'neutral',
}

/**
 * Who is travelling where, and what they are advanced for it.
 *
 * Where no per-diem rate covers the destination the advance is left blank and the reason is
 * shown on the row. A daily allowance is a policy figure and often tax-relevant; a number
 * this system invented would be paid to a real person and defended by nobody.
 */
export function TravelRequestsPage() {
  useBreadcrumb()
  const requests = useTravelRequests()
  const rates = usePerDiemRates()
  const { raise, submit, approve } = useTravelMutations()

  const [draft, setDraft] = useState<{
    employeeId: string; destination: string; countryCode: string; purpose: string
    departsOn: string; returnsOn: string; advanceRequested: string; currency: string
  } | null>(null)
  const [error, setError] = useState<string | null>(null)

  const destinations = [...new Set(rates.data?.map((rate) => rate.destination) ?? [])]

  return (
    <div className="space-y-4">
      <PageHeader
        title="Travel requests"
        subtitle="Trips, the days away, and the advance against them."
        actions={
          <Button
            type="button"
            onClick={() => {
              setError(null)
              setDraft({
                employeeId: '', destination: '', countryCode: '', purpose: '',
                departsOn: '', returnsOn: '', advanceRequested: '', currency: 'AED',
              })
            }}
          >
            <Plus className="size-4" aria-hidden="true" />
            New request
          </Button>
        }
      />

      {error && (
        <p className="rounded-md border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800">
          {error}
        </p>
      )}

      <Card>
        {requests.isPending && <Skeleton className="m-4 h-24" />}
        {requests.isError && (
          <ErrorState error={requests.error} onRetry={() => void requests.refetch()} />
        )}
        {requests.isSuccess && requests.data.length === 0 && (
          <EmptyState
            title="Nobody is travelling"
            description="A request records where and when, works out the per-diem from the configured rate, and goes for approval."
          />
        )}
        {requests.isSuccess && requests.data.length > 0 && (
          <div className="overflow-x-auto overscroll-x-contain">
            <table className="w-full text-sm">
              <thead className="border-b border-slate-200 text-slate-600">
                <tr>
                  <th className="p-3 text-start font-medium">Reference</th>
                  <th className="p-3 text-start font-medium">Person</th>
                  <th className="p-3 text-start font-medium">Where</th>
                  <th className="p-3 text-start font-medium">When</th>
                  <th className="p-3 text-end font-medium">Days</th>
                  <th className="p-3 text-end font-medium">Advance</th>
                  <th className="p-3 text-start font-medium">Status</th>
                  <th className="p-3 text-end font-medium" />
                </tr>
              </thead>
              <tbody>
                {requests.data.map((request) => (
                  <tr key={request.id} className="border-b border-slate-100 align-top">
                    <td className="p-3 font-mono">{request.reference}</td>
                    <td className="p-3">{request.employeeName ?? '—'}</td>
                    <td className="p-3">{request.destination}</td>
                    <td className="p-3 text-slate-600">
                      {request.departsOn} → {request.returnsOn}
                    </td>
                    <td className="p-3 text-end">{request.daysAway}</td>
                    <td className="p-3 text-end">
                      {request.advanceRequested != null ? (
                        <CurrencyValue
                          value={request.advanceRequested}
                          currency={request.currency ?? undefined}
                        />
                      ) : (
                        <span
                          className="text-amber-700"
                          title={request.perDiemUnavailableReason ?? undefined}
                        >
                          Not set
                        </span>
                      )}
                      {request.perDiemUnavailableReason && (
                        <p className="mt-1 max-w-xs text-start text-xs font-normal text-amber-700">
                          {request.perDiemUnavailableReason}
                        </p>
                      )}
                    </td>
                    <td className="p-3">
                      <StatusBadge
                        status={request.status}
                        tone={STATUS_TONE[request.status as TravelStatus] ?? 'neutral'}
                        label={TRAVEL_STATUS_LABELS[request.status as TravelStatus]
                          ?? request.status}
                      />
                    </td>
                    <td className="p-3 text-end">
                      {request.status === 'DRAFT' && (
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => {
                            setError(null)
                            void (async () => {
                              try {
                                await submit.mutateAsync(request.id)
                              } catch (caught) {
                                setError(toApiProblem(caught)?.detail
                                  ?? 'That request could not be submitted')
                              }
                            })()
                          }}
                        >
                          Submit
                        </Button>
                      )}
                      {request.status === 'SUBMITTED' && (
                        <Button
                          type="button"
                          onClick={() => {
                            setError(null)
                            void (async () => {
                              try {
                                await approve.mutateAsync({ id: request.id })
                              } catch (caught) {
                                setError(toApiProblem(caught)?.detail
                                  ?? 'That request could not be approved')
                              }
                            })()
                          }}
                        >
                          Approve
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <p className="flex items-start gap-2 text-sm text-slate-600">
        <Plane className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
        Days away count both the departure and the return day. Expenses from a trip can be
        claimed against the request, so the claim and the trip stay linked.
      </p>

      <FormDialog
        open={draft != null}
        onOpenChange={(open) => { if (!open) setDraft(null) }}
        size="lg"
        title="New travel request"
        description="The per-diem is worked out from the rate in force for the destination on the departure date."
        submitLabel="Save as draft"
        loading={raise.isPending}
        error={error}
        onSubmit={() => {
          if (!draft) return
          void (async () => {
            setError(null)
            try {
              await raise.mutateAsync({
                employeeId: draft.employeeId,
                destination: draft.destination,
                countryCode: draft.countryCode || null,
                purpose: draft.purpose || null,
                departsOn: draft.departsOn,
                returnsOn: draft.returnsOn,
                advanceRequested: draft.advanceRequested
                  ? Number(draft.advanceRequested) : null,
                currency: draft.currency || null,
              })
              setDraft(null)
            } catch (caught) {
              setError(toApiProblem(caught)?.detail ?? 'That request could not be saved')
            }
          })()
        }}
      >
        {draft && (
          <div className="space-y-3">
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <Label htmlFor="travel-employee">Person</Label>
                <EmployeePicker
                  id="travel-employee"
                  value={draft.employeeId}
                  onChange={(employeeId) => setDraft({ ...draft, employeeId: employeeId ?? '' })}
                />
              </div>
              <div>
                <Label htmlFor="travel-destination">Destination</Label>
                <Input
                  id="travel-destination" list="travel-destinations" value={draft.destination}
                  onChange={(event) => setDraft({ ...draft, destination: event.target.value })}
                />
                <datalist id="travel-destinations">
                  {destinations.map((destination) => (
                    <option key={destination} value={destination} />
                  ))}
                </datalist>
                <p className="mt-1 text-xs text-slate-600">
                  Matching a destination that has a rate fills the advance in automatically.
                </p>
              </div>
              <div>
                <Label htmlFor="travel-departs">Departs</Label>
                <DatePicker
                  id="travel-departs" value={draft.departsOn}
                  onChange={(event) => setDraft({ ...draft, departsOn: event.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="travel-returns">Returns</Label>
                <DatePicker
                  id="travel-returns" value={draft.returnsOn}
                  onChange={(event) => setDraft({ ...draft, returnsOn: event.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="travel-advance">Advance asked for</Label>
                <Input
                  id="travel-advance" type="number" value={draft.advanceRequested}
                  onChange={(event) =>
                    setDraft({ ...draft, advanceRequested: event.target.value })}
                />
                <p className="mt-1 text-xs text-slate-600">
                  Left blank, the per-diem is used where a rate exists.
                </p>
              </div>
              <div>
                <Label htmlFor="travel-currency">Currency</Label>
                <Input
                  id="travel-currency" maxLength={3} value={draft.currency}
                  onChange={(event) =>
                    setDraft({ ...draft, currency: event.target.value.toUpperCase() })}
                />
              </div>
            </div>
            <div>
              <Label htmlFor="travel-purpose">Purpose</Label>
              <Input
                id="travel-purpose" value={draft.purpose}
                onChange={(event) => setDraft({ ...draft, purpose: event.target.value })}
              />
            </div>
            {destinations.length === 0 && (
              <p className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
                <Info className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
                No per-diem rate is configured yet, so every advance has to be entered by hand.
              </p>
            )}
          </div>
        )}
      </FormDialog>
    </div>
  )
}
